import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import requests from '../api/requests';
import { useDropzone } from 'react-dropzone';

const Duets  = ({ 
  index, 
  value = {}, 
  onChange, 
  onRemove,
  isSearching = false,
  pendingDownloads = {},
  downloadedVideos = {},
  existingDuets = []
}) => {
  const [isVideoSearching, setIsVideoSearching] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [videoUrl, setVideoUrl] = useState(value.video || '');

  useEffect(() => {
    // When the component mounts, set the videoUrl if it's an existing duet
    if (value.video && value.video.length > 0) {
      setVideoUrl(value.video);
    }
  }, [value.video]);

  const handleChange = (field, fieldValue) => {
    if (onChange) {
      onChange(index, field, fieldValue);
    } else {
      console.log(`Duet ${index}: onChange is null/undefined!`);
    }
  };

  const handleBatchChange = (updates) => {
    if (onChange) {
      onChange(index, 'batch', updates);
    } else {
      console.log(`Duet ${index}: onChange is null/undefined!`);
    }
  };

  const handleVideoSearch = async (url) => {
    try {
      setIsVideoSearching(true);
      setVideoUrl(url);
      handleChange("video", url);
      
      if (!url) return;

      // Determine if it's a TikTok or Instagram URL
      const isTikTok = url.includes('tiktok.com');
      const isInstagram = url.includes('instagram.com');

      // If it's a new social media URL, clear the selected video
      if (isTikTok || isInstagram) {
        setSelectedVideo(null);
      } else {
        // If it's not a social media URL, just update the value without searching
        handleChange("video", url);
        return;
      }

      let response;
      if (isTikTok) {
        response = await requests.tiktok.searchVideoByUrl({ videoUrl: url });
      } else {
        response = await requests.instagram.searchVideoByUrl({ videoUrl: url });
      }

      if (response.data) {
        if (isInstagram && response.data[0]?.urls?.[0]) {
          const videoData = {
            ...response.data[0],
            type: 'instagram'
          };
          setSelectedVideo(videoData);
          const cover = response.data[0].pictureUrl || '';
          handleChange("cover", cover);
          const author = response.data[0].meta?.author || response.data[0].owner?.username || response.data[0].user?.username || '';
          if (author) handleChange("author", author);
        } else {
          const videoData = {
            ...response.data,
            type: 'tiktok'
          };
          setSelectedVideo(videoData);
          handleChange("cover", response.data.cover || '');
          if (response.data.author) handleChange("author", response.data.author);
        }
      }
    } catch (error) {
      console.error('Error searching video:', error);
      toast.error('Failed to search video');
    } finally {
      setIsVideoSearching(false);
    }
  };

  const handleDownloadVideo = async () => {
    if (!selectedVideo) return;

    try {
      setIsVideoSearching(true);
      const response = await requests.tiktok.downloadVideo({
        videoUrl: selectedVideo.type === 'instagram' ? selectedVideo.urls[0].url : selectedVideo.videoUrl,
        videoMetadata: selectedVideo.type === 'instagram' ? {
          title: selectedVideo.meta?.title || '',
          author: value.author || '',
          cover: selectedVideo.pictureUrl || '',
          description: selectedVideo.meta?.description || '',
          playUrl: selectedVideo.urls[0].url,
          source: 'instagram'
        } : {
          title: selectedVideo.title || '',
          author: value.author || '',
          cover: selectedVideo.cover || '',
          duration: selectedVideo.duration || '',
          views: selectedVideo.views || 0,
          description: selectedVideo.description || '',
          playUrl: selectedVideo.playUrl || '',
          source: 'tiktok'
        }
      });

      if (response.data?.videoUrl) {
        const author = value.author || (selectedVideo?.type === 'instagram'
          ? (selectedVideo?.meta?.author || selectedVideo?.owner?.username || selectedVideo?.user?.username)
          : selectedVideo?.author) || '';
        handleBatchChange({
          video: response.data.videoUrl,
          cover: response.data.coverUrl || null,
          ...(author ? { author } : {})
        });
        setSelectedVideo(null);
        setVideoUrl('');
        toast.success('Video downloaded successfully');
      }
    } catch (error) {
      console.error('Error downloading video:', error);
      toast.error('Failed to download video');
    } finally {
      setIsVideoSearching(false);
    }
  };

  // Video dropzone
  const { getRootProps: getVideoRootProps, getInputProps: getVideoInputProps, isDragActive: isVideoDragActive } = useDropzone({
    onDrop: (acceptedFiles) => {
      console.log(`Duets: File dropped on Video ${index + 1} (index: ${index})`);
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        const fileUrl = URL.createObjectURL(file);
        handleBatchChange({
          video: fileUrl,
          videoFile: file
        });
      }
    },
    accept: {
      'video/*': ['.mp4', '.mov', '.avi', '.mkv', '.webm']
    },
    multiple: false
  });

  // Cover dropzone
  const { getRootProps: getCoverRootProps, getInputProps: getCoverInputProps, isDragActive: isCoverDragActive } = useDropzone({
    onDrop: (acceptedFiles) => {
      console.log(`Duets: Cover dropped on Duet ${index + 1} (index: ${index})`);
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        const fileUrl = URL.createObjectURL(file);
        handleBatchChange({
          cover: fileUrl,
          coverFile: file
        });
      }
    },
    accept: {
      'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.webp']
    },
    multiple: false
  });

  // Helper function to check if a URL is an external link
  const isExternalLink = (url) => {
    return url && (url.startsWith('http://') || url.startsWith('https://'));
  };

  return (
    <div className="border p-4 rounded-lg mb-4">
      <div className="flex justify-between items-center mb-4">
        <h4 className="font-medium">Duet {index + 1}</h4>
        <button
          type="button"
          onClick={() => onRemove && onRemove(index)}
          className="text-red-500 hover:text-red-700"
        >
          Remove
        </button>
      </div>
      <div>
        <div className="flex gap-2">
          <input
            type="text"
            value={videoUrl}
            onChange={(e) => handleVideoSearch(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            placeholder="Enter TikTok or Instagram URL"
          />
          {isVideoSearching && (
            <div className="mt-1">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
            </div>
          )}
        </div>
        
        {/* Author Input Field */}
        <div className="mt-3">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Author
          </label>
          <div className="flex items-center gap-2">
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="text-gray-500 text-sm">@</span>
              </div>
              <input
                type="text"
                value={value.author || ''}
                onChange={(e) => handleChange("author", e.target.value)}
                className="block w-full pl-7 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Enter author username"
              />
            </div>
            {selectedVideo && (
              <span className="text-xs text-gray-500 px-2 py-1 bg-gray-100 rounded">
                {selectedVideo.type === 'instagram' ? 'Instagram' : 'TikTok'}
              </span>
            )}
          </div>
          {selectedVideo?.author && (
            <p className="text-xs text-gray-500 mt-1">
              Suggested from search: @{selectedVideo.author}
            </p>
          )}
        </div>

        {/* Download Button - Show when video is found */}
        {selectedVideo && !value.video?.startsWith('uploads/') && (
          <div className="mt-4">
            <button
              onClick={handleDownloadVideo}
              className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed"
              disabled={isVideoSearching}
            >
              <svg 
                className="w-5 h-5" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" 
                />
              </svg>
              {isVideoSearching ? 'Downloading...' : 'Download Video'}
            </button>
          </div>
        )}

        {/* Video and Cover Preview - Show when video is downloaded from URL */}
        {value.video && !value.video.startsWith('blob:') && (
          <div className="mt-4 grid grid-cols-2 gap-4">
            {/* Video Preview */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Downloaded Video</label>
              <video
                key={value.video}
                src={
                  isExternalLink(value.video)
                    ? value.video
                    : `${process.env.REACT_APP_BASE_URL || ''}${value.video.startsWith('/') ? '' : '/'}${value.video}`
                }
                controls
                className="w-full h-32 object-cover rounded border"
                onError={(e) => {
                  console.error('Video error:', e);
                  toast.error('Error loading video. The video may have been moved or deleted.');
                }}
              />
            </div>
            
            {/* Cover Preview */}
            {value.cover && (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Downloaded Cover</label>
                <img
                  src={
                    isExternalLink(value.cover)
                      ? value.cover
                      : `${process.env.REACT_APP_BASE_URL || ''}${value.cover.startsWith('/') ? '' : '/'}${value.cover}`
                  }
                  alt="Cover"
                  className="w-full h-32 object-cover rounded border"
                  onError={(e) => {
                    console.error('Cover error:', e);
                    toast.error('Error loading cover image.');
                  }}
                />
              </div>
            )}
          </div>
        )}

        {/* OR separator */}
        <div className="flex items-center my-4">
          <div className="flex-grow border-t border-gray-300"></div>
          <span className="mx-2 text-gray-500 font-semibold">OR</span>
          <div className="flex-grow border-t border-gray-300"></div>
        </div>
        
        {/* Video and Cover Upload Section - Side by Side */}
        <div className="grid grid-cols-2 gap-4">
          {/* Video Upload Section */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Video</label>
            {/* Dropzone for video upload */}
            <div {...getVideoRootProps()} className="border-2 border-dashed border-gray-300 p-4 rounded cursor-pointer text-center bg-gray-50 hover:bg-gray-100 min-h-[120px] flex items-center justify-center">
              <input {...getVideoInputProps()} />
              {isVideoDragActive ? (
                <p className="text-blue-500">Drop the video file here...</p>
              ) : (
                <div className="text-gray-500">
                  <svg className="w-8 h-8 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  <p className="text-sm">Drag & drop video file here, or click to select</p>
                </div>
              )}
            </div>
            
            {/* Video Preview - Only for uploaded files (blob URLs) */}
            {value.video && value.video.startsWith('blob:') && (
              <div className="mt-2 relative">
                <video
                  key={value.video}
                  src={value.video}
                  controls
                  className="w-full h-32 object-cover rounded"
                  onError={(e) => {
                    console.error('Video error:', e);
                    toast.error('Error loading video. The video may have been moved or deleted.');
                  }}
                />
                {/* Remove video button */}
                <button
                  type="button"
                  onClick={() => {
                    handleBatchChange({
                      video: null,
                      videoFile: null
                    });
                  }}
                  className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                  title="Remove video"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}
          </div>

          {/* Cover Upload Section */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Cover</label>
            {/* Dropzone for cover upload */}
            <div {...getCoverRootProps()} className="border-2 border-dashed border-gray-300 p-4 rounded cursor-pointer text-center bg-gray-50 hover:bg-gray-100 min-h-[120px] flex items-center justify-center">
              <input {...getCoverInputProps()} />
              {isCoverDragActive ? (
                <p className="text-blue-500">Drop the cover image here...</p>
              ) : (
                <div className="text-gray-500">
                  <svg className="w-8 h-8 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p className="text-sm">Drag & drop cover image here, or click to select</p>
                </div>
              )}
            </div>
            
            {/* Cover Preview - Only for uploaded files (blob URLs) */}
            {value.cover && value.cover.startsWith('blob:') && (
              <div className="mt-2 relative">
                <img
                  src={value.cover}
                  alt="Cover"
                  className="w-full h-32 object-cover rounded"
                  onError={(e) => {
                    console.error('Cover error:', e);
                    toast.error('Error loading cover image.');
                  }}
                />
                {/* Remove cover button */}
                <button
                  type="button"
                  onClick={() => {
                    handleBatchChange({
                      cover: null,
                      coverFile: null
                    });
                  }}
                  className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                  title="Remove cover"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default Duets; 